import { router } from '@inertiajs/react'

// THE EXPANDED MAP HAS A URL.
//
// `/explore` is not a second map any more: it is the address of the home map
// with the map filling the screen. Expanding pushes it, collapsing pushes `/`
// back, and browser back does the same thing the collapse button does. A direct
// hit on `/explore#…` — every link, bookmark and native-app web link ever
// shared — renders the home page already expanded at the view the hash asked
// for. See docs/context/project_explore_is_expanded_home.md.
//
// The map instance is the reason this is a routing change rather than a page:
// the in-place expand is what lets the camera, the tiles and the WebGL context
// survive the toggle, and nothing here is allowed to cost that.

// The z-index the maximized map stands at, repeated rather than imported.
//
// `MAXIMIZED_Z` lives in _chartMapMode.js, and that module reaches the canvas
// icon generator through _stationIcons — a lazily-loaded graph that has no
// business in the eager bundle just so the loading placeholder can name a
// number. One integer, one test (expandUrl.test.js) that fails the moment the
// two disagree.
export const EXPANDED_MAP_Z = 400

// Paths compare with any trailing slash removed, so "/" and "" are the same
// place and "/es/explore/" is "/es/explore". Rails draws these routes without
// one; a reader can type one and a share sheet can add one.
export function samePath(a, b) {
  const strip = (p) => String(p ?? '').replace(/\/+$/, '')
  return strip(a) === strip(b)
}

// Which state the address bar is asking for. Anything that is not the expanded
// path is collapsed — there are exactly two, and "not /explore" is the only
// honest reading of a path we did not write.
export function expandedFromPath(pathname, expandedPath) {
  return samePath(pathname, expandedPath)
}

// The hash slots carry 4 decimal places of latitude (~11 m) and 2 of zoom, so a
// view that has just round-tripped through the URL comes back within half of
// one. Inside those the entry we landed on IS the view already drawn, and
// moving the camera would be a visible twitch recording no change.
const COORD_EPSILON = 1e-4
const ZOOM_EPSILON = 1e-2

export function viewDiffers(current, view) {
  if (!current || !view) return false
  return (
    Math.abs(current.latitude - view.latitude) > COORD_EPSILON ||
    Math.abs(current.longitude - view.longitude) > COORD_EPSILON ||
    Math.abs(current.zoom - view.zoom) > ZOOM_EPSILON
  )
}

// ---- WHAT THE BROWSER RESTORED, READ BEFORE ANYTHING REPAIRS IT -----------
//
// A `popstate` listener on this page cannot trust `window.location`, and that
// is not a subtlety — it is the first thing that broke. Inertia's listener is
// registered when the app boots and the map's when it mounts, so Inertia's runs
// first; a microtask checkpoint happens between two listeners of the same
// event, which is long enough for the queued `replaceState` its repair issues
// to land. Measured (Chromium, 2026-09-16): expand, then back, and the map read
// `/explore` out of an address bar the browser had restored to `/` a moment
// earlier — so it kept the map expanded and put `/explore` back.
//
// So the answer is recorded by a listener registered BEFORE Inertia's, from the
// app entrypoint, where "first" is module evaluation order rather than a hope.
// It records and does nothing else.
let restored = null
let watching = false

export function watchRestoredUrl() {
  if (typeof window === 'undefined' || watching) return
  watching = true
  window.addEventListener('popstate', () => {
    const { pathname, search, hash } = window.location
    restored = `${pathname}${search}${hash}`
  })
}

// The URL the most recent `popstate` restored, or null if none has happened and
// if the watcher was never installed — a caller that falls back to
// `window.location` is then exactly as well off as it was before this existed.
export function restoredUrl() {
  return restored
}

// ---- WRITING HISTORY: ONE PUSH PER GESTURE, NEVER PER FRAME ----------------
//
// `history.replaceState` is capped at 100 calls per 10 seconds in WebKit and
// THROWS past the cap — the crash of 2026-09-12, and the whole reason _time.js
// throttles the hash writer to one write per 500 ms. `pushState` is counted
// against the same budget, so it is called from a click handler and from
// nowhere else: a gesture a reader can produce a few times a second at worst,
// where a map `idle` fires sixty times a second. The throttle stays exactly as
// it is; this does not go near it.
//
// Wrapped for the same reason the hash writer is wrapped: a browser rate limit
// is not an application error, and an address bar that goes stale is cosmetic
// where a map that dies is not.
export function pushSurfaceUrl(url) {
  try {
    window.history.pushState(null, '', url)
    return true
  } catch {
    return false
  }
}

// ---- AND TELLING INERTIA, WHICH OWNS THE OTHER HISTORY --------------------
//
// Two history systems share this page. Inertia keeps the page it last swapped,
// and on a `popstate` onto an entry carrying no state of its own it REPAIRS the
// address bar from that page's url. Every entry this map has touched carries no
// state: the hash writer calls `replaceState(null, …)`, which drops Inertia's
// state as a side effect, and it has done so on both these pages since the map
// started syncing the URL. So after an expand, Inertia's repair would write
// `/` over the `/explore` a reader just arrived at.
//
// It cannot be pre-empted — its listener is registered when the app boots and
// ours when the map mounts — but it CAN be followed, because both writes go
// through Inertia's own FIFO queue. A `replace` issued from our handler is
// queued behind the repair and has the last word, whatever order the microtasks
// inside either of them happen to run in.
//
//   * `preserveState` keeps the React key, so the page does not remount and
//     neither does the map. That is the whole point of folding `/explore` into
//     this page instead of linking to it.
//   * `preserveScroll` keeps `Scroll.reset()` from firing, which would throw
//     the reader to the top of the page.
//   * a `replace` fires no `navigate` event, so this correction is invisible to
//     analytics; the gesture's own `pushState` is the one Plausible counts.
//
// Then the entry's state is dropped again, deliberately. Inertia remounts the
// component on any `popstate` onto an entry that DOES carry state
// (`setQuietly(…, { preserveState: false })`), so leaving one behind would make
// the browser's forward button rebuild the map — the one thing this must not
// do. State-less is the branch that keeps the instance, and it is the branch
// every entry on these two pages is already in.
export function syncInertiaUrl(url) {
  router.replace({
    url,
    preserveState: true,
    preserveScroll: true,
    onFinish: () => {
      try {
        window.history.replaceState(null, '', url)
      } catch {
        // See pushSurfaceUrl.
      }
    },
  })
}
